import React from "react";
import Card from "components/card";
import CommonModal from "components/modals/Modal";
import DeleteModal from "./components/modals/DeleteModal";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { deleteBranch, getBranch } from "api/BranchAPI";
import { createPortal } from "react-dom";
import { Spinner, useDisclosure } from "@chakra-ui/react";
import { IBranch } from "./types/branch.interface";
import { FiSearch } from "react-icons/fi";
import { IGoods } from "../goods/types/goods.interface";
import { assignGoodToBranch, getGoods } from "api/GoodsAPI";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import debounce from "lodash.debounce";
import Dropdown from "components/dropdown";
import { FaBox, FaBoxes, FaTrash } from "react-icons/fa";
import ProductCardAssign from "components/card/ProductCardAssign";
import Button from "components/button/Button";
import { PiDotsThreeBold } from "react-icons/pi";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";

const AssignProductBranch: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isOpen, onClose } = useDisclosure();
  const [branch, setBranch] = React.useState<IBranch>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [cart, setCart] = React.useState<
    {
      product_id: number;
      name: string;
      stock: number;
    }[]
  >([]);
  const [goods, setGoods] = React.useState<IGoods[]>([]);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    ob: NumberParam,
    search: StringParam,
    limit: NumberParam,
  });

  const orderBy: number = null;
  const limit: number = null;
  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [nextPage, setNextPage] = React.useState<number>(null);
  const [prevPage, setPrevPage] = React.useState<number>(null);
  const [lastPage, setLastPage] = React.useState<number>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageState, setPageState] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });
  const [pageNumbers, setPageNumbers] = React.useState<number[]>([]);
  const [open, setOpen] = React.useState(false);
  const modalAction: "show" | "confirmation" = "confirmation";

  const getBranchDetail = () => {
    const promise = getBranch(slug);
    promise
      .then((res) => {
        if (res.status === 200) setBranch(res.data.data);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Gagal mengambil data toko!"
        );
      });
  };

  const jumpForwardPage = () => {
    setPageState({
      startIdx: pageState.endIdx,
      endIdx: pageState.endIdx + 5,
    });
  };

  const jumpBackwardPage = () => {
    setPageState({
      startIdx: pageState.startIdx - 5,
      endIdx: pageState.endIdx - 5,
    });
  };

  const handleNextPage = () => {
    if (currentPage === lastPage) return;
    setCurrentPage(nextPage);
    getGoodsWithParams(nextPage, orderBy, searchKey, limit);
  };

  const handlePrevPage = () => {
    if (currentPage === 1) return;
    setCurrentPage(prevPage);
    getGoodsWithParams(prevPage, orderBy, searchKey, limit);
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getGoodsWithParams(currentPage, orderBy, e.target.value, limit);
    setQuery({
      page: currentPage,
      ob: query.ob,
      search: e.target.value === "" ? null : e.target.value,
      limit: query.limit,
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGoodsWithParams = (
    page?: number,
    order?: number,
    keyword?: string,
    limit?: number
  ): void => {
    setIsLoading(true);
    const promise = getGoods(page, order, keyword, limit);

    promise
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          setGoods(res.data.data.items);
          setNextPage(res.data.data.meta.next_page);
          setPrevPage(res.data.data.meta.prev_page);
          setLastPage(res.data.data.meta.last_page);
          setCurrentPage(res.data.data.meta.current_page);
          setPageNumbers(
            Array.from(Array(res.data.data.meta.last_page + 1).keys()).slice(1)
          );
          if (res.data.data.meta.current_page === pageState.endIdx + 1) {
            setPageState({
              startIdx: pageState.endIdx,
              endIdx: pageState.endIdx + 5,
            });
          } else if (res.data.data.meta.current_page === pageState.startIdx) {
            setPageState({
              startIdx: pageState.startIdx - 5,
              endIdx: pageState.endIdx - 5,
            });
          }
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal memuat barang!");
      });
  };

  const handleDeleteBranch = () => {
    const promise = deleteBranch(slug);
    toast.promise(promise, {
      loading: "Menghapus toko...",
      success: () => {
        navigate("/admin/branches");
        return "Berhasil menghapus toko";
      },
      error: (err) => {
        return err?.response?.data?.message || "Gagal menghapus toko";
      },
    });
  };

  const handleAddToCart = (product_id: number, name: string, stock: number) => {
    setCart([
      ...cart,
      {
        product_id,
        name,
        stock,
      },
    ]);
  };

  const handleSubmitCart = () => {
    // remove name from cart state
    const products = cart.map((item) => {
      return {
        product_id: item.product_id,
        stock: parseInt(item.stock.toString()),
      };
    });
    const payload = {
      store_id: branch?.id,
      products,
    };

    const promise = assignGoodToBranch(payload);
    toast.promise(promise, {
      loading: "Menambahkan barang ke toko...",
      success: () => {
        setCart([]);
        navigate(`/admin/branches/${slug}`);
        return "Berhasil menambahkan barang ke toko";
      },
      error: (err) => {
        return err?.response?.data?.message || "Gagal menambahkan barang";
      },
    });
  };

  React.useEffect(() => {
    if (slug) {
      getBranchDetail();
      getGoodsWithParams(query.page, query.ob, query.search, query.limit);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card extra={"w-full h-full px-6 pb-3 mt-3"}>
        <header className="relative flex items-center justify-between pt-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Assign Barang ke{" "}
            <span className="text-brand-500">{branch?.name}</span>
          </div>
          <Dropdown
            button={
              <button
                onClick={() => setOpen(!open)}
                className={`linear relative flex items-center 
               justify-center rounded-lg bg-lightPrimary p-2 text-xl font-bold text-brand-500 transition duration-200 hover:cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10`}
              >
                <FaBoxes className="mr-2 h-6 w-6" />
                <span className="text-sm">{cart.length} Barang</span>
              </button>
            }
            animation={
              "origin-top-right transition-all duration-300 ease-in-out"
            }
            classNames={`top-11 right-0 w-max`}
            children={
              <div className="shadow-shadow-700 z-50 flex w-max flex-col gap-2 rounded-xl bg-white px-4 py-3 text-sm shadow-xl dark:!bg-navy-700 dark:shadow-none">
                {cart.length > 0 ? (
                  cart?.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 dark:bg-white/20">
                          <FaBox className="h-5 w-5 text-gray-400 dark:text-white" />
                        </div>
                        <div className="ml-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-white">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-white">
                            {item.stock} pcs
                          </p>
                        </div>
                      </div>
                      <Button
                        size="fit"
                        variant="gray"
                        children={<FaTrash className="h-4 w-4 text-red-500" />}
                        onClick={() => {
                          const newCart = cart.filter((_, i) => i !== idx);
                          setCart(newCart);
                        }}
                      ></Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 dark:text-white">
                    Keranjang kosong
                  </p>
                )}
                {cart.length > 0 && (
                  <Button
                    size="full"
                    variant="primary"
                    children="Tambahkan"
                    extra="!py-2 !text-sm mt-3"
                    onClick={() => handleSubmitCart()}
                  />
                )}
              </div>
            }
          />
        </header>

        <div className="mt-10">
          <div className="flex h-12 w-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Cari nama barang..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey || query.search}
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
          <div className="mt-5">
            {!isLoading ? (
              goods?.length > 0 ? (
                <div className="z-20 grid grid-cols-1 gap-5 md:grid-cols-3">
                  {goods.map((good, idx) => (
                    <div key={idx}>
                      <ProductCardAssign
                        id={good.id}
                        title={good.name}
                        category={good.category?.name}
                        price={good.price.original_price}
                        sku={good.sku}
                        description={good.description}
                        thumbnail={good.image_thumbnail}
                        images={good.images}
                        slug={good.slug}
                        weight={good.weight}
                        onAssign={handleAddToCart}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-80 items-center justify-center">
                  <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                    {!searchKey
                      ? "Belum ada barang yang ditambahkan"
                      : "Barang tidak ditemukan"}
                  </p>
                </div>
              )
            ) : (
              <div className="flex h-80 w-full flex-col items-center justify-center">
                <Spinner size="xl" className="text-brand-400" />
              </div>
            )}
          </div>
          <div className="pagination my-5 flex w-full justify-center">
            <div className="pagination__item flex items-center gap-2">
              <button
                className="pagination__button"
                onClick={() => handlePrevPage()}
              >
                <BiChevronLeft className="text-xl" />
              </button>
              <div className="flex items-center justify-center gap-1">
                {pageState.startIdx >= 1 && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpBackwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
                {pageNumbers
                  .slice(pageState.startIdx, pageState.endIdx)
                  .map((page, idx) => (
                    <button
                      className={`pagination__button ${
                        currentPage === page ? "pagination__button--active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        getGoodsWithParams(page, orderBy, searchKey, limit);
                        setQuery({
                          page: page,
                          ob: orderBy,
                          search: searchKey,
                          limit: limit,
                        });
                      }}
                      key={idx}
                    >
                      {page}
                    </button>
                  ))}
                {pageState.endIdx <= lastPage && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpForwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
              </div>
              <button
                className="pagination__button"
                onClick={() => handleNextPage()}
              >
                <BiChevronRight className="text-xl" />
              </button>
            </div>
          </div>
        </div>
      </Card>
      {createPortal(
        <CommonModal
          size="2xl"
          children={
            <div>
              {modalAction === "confirmation" && (
                <DeleteModal
                  title="Apakah anda yakin ingin menghapus toko ini?"
                  onCancel={onClose}
                  onDelete={handleDeleteBranch}
                />
              )}
            </div>
          }
          isOpen={isOpen}
          onClose={onClose}
        />,
        document.body
      )}
    </>
  );
};

export default AssignProductBranch;
